import type { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { BridgeObservabilityEvent } from '@atlassian/rovo-content-bridge-api/observability-types';

import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { AIFC_BRIDGE_REGISTRATION_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

const ANALYTICS_SOURCE = 'aifcBridgeObservability';
const ANALYTICS_ACTION_SUBJECT = 'rovoContentBridge';

type CreateAnalyticsEvent = ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'];

export interface BridgeObservabilityDispatchDeps {
	experienceTracker: ExperienceTrackerAPI;
	createAnalyticsEvent: CreateAnalyticsEvent;
	submitError: (error: Error) => void;
}

/**
 * Maps a single normalised bridge observability signal onto the `aifc-bridge-registration`
 * Experience and operational analytics. Kept as a pure function (dependencies injected) so the
 * mapping is exercised directly and synchronously, independent of the hook's effect plumbing.
 */
export const dispatchBridgeObservabilitySignal = (
	event: BridgeObservabilityEvent,
	{ experienceTracker, createAnalyticsEvent, submitError }: BridgeObservabilityDispatchDeps,
): void => {
	switch (event.signal) {
		case 'registration-pending':
			experienceTracker.start({
				name: AIFC_BRIDGE_REGISTRATION_EXPERIENCE,
				id: event.clientId,
			});
			break;
		case 'registration-succeeded':
			experienceTracker.succeed({ name: AIFC_BRIDGE_REGISTRATION_EXPERIENCE });
			break;
		case 'registration-aborted':
			experienceTracker.abort({
				name: AIFC_BRIDGE_REGISTRATION_EXPERIENCE,
				reason: 'Bridge client torn down before registering',
			});
			break;
		case 'registration-gap': {
			const error = new Error(
				`Rovo content bridge client "${event.clientId}" did not register within ${event.waitedMs}ms; streamed messages were left unconsumed`,
			);
			submitError(error);
			experienceTracker.stopOnError({ name: AIFC_BRIDGE_REGISTRATION_EXPERIENCE, error });
			break;
		}
		case 'message-queued':
		case 'message-dropped':
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: ANALYTICS_SOURCE,
					action: event.signal === 'message-dropped' ? 'messageDropped' : 'backpressure',
					actionSubject: ANALYTICS_ACTION_SUBJECT,
					attributes: {
						clientId: event.clientId,
						queueDepth: event.queueDepth,
						reason: event.reason,
					},
				},
			}).fire();
			break;
		default:
			break;
	}
};
